import { useCallback, useEffect, useState } from "react";
import { Gift, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { fmtDate } from "@/pages/Dashboard";
import { LedgerTable, Panel, PurchaseTable, signed } from "@/pages/Billing";
import { ReadingsTable } from "@/pages/admin/AdminCosts";

const field = "h-10 rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-amber-500/60";

function GiftForm({ onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!Number.isInteger(n) || n === 0) return setError("Enter a whole, non-zero number of credits (use - to remove)");
    if (!reason.trim()) return setError("A reason is required");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/home/gift", { amount: n, reason: reason.trim() });
      toast.success(`${signed(n)} credits applied. New balance ${data.balance}`);
      setAmount("");
      setReason("");
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="gift-form" className="space-y-3 p-6">
      <div className="flex flex-wrap gap-3">
        <input data-testid="gift-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="+20 or -5" inputMode="numeric" className={`${field} w-32 font-mono-num`} />
        <input data-testid="gift-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown in the credit history)" className={`${field} min-w-[220px] flex-1`} />
        <button data-testid="gift-submit-button" disabled={busy}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Apply
        </button>
      </div>
      {error && <p data-testid="gift-error" className="text-sm text-red-300">{error}</p>}
    </form>
  );
}

function RemoveOthers({ count, onDone }) {
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/remove-other-companies", { confirm: word });
      setRes(data);
      setWord("");
      toast.success(`Removed ${data.removed.companies || 0} other companies. Backup ${data.backup_id}`);
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={run} data-testid="remove-others-form" className="space-y-3 p-6">
      <p className="text-sm text-slate-400">
        One-time clean-up: backs up, then deletes every company except Viewlux, with their users, quotes, readings, price lists, product lines, shapes, credits and payments.
        <span data-testid="remove-others-count" className="ml-1 font-semibold text-slate-100">{count} other {count === 1 ? "company" : "companies"} now.</span>
      </p>
      <div className="flex flex-wrap gap-3">
        <input data-testid="remove-others-confirm-input" value={word} onChange={(e) => setWord(e.target.value)} placeholder="Type REMOVE" className={`${field} w-44 font-mono-num`} />
        <button data-testid="remove-others-submit-button" disabled={busy || !count}
          className="inline-flex h-10 items-center gap-2 rounded-full border border-red-500/60 px-5 text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remove all other companies
        </button>
      </div>
      {res && (
        <p data-testid="remove-others-result" className="text-xs text-slate-400">
          Backup {res.backup_id}{res.backup_file ? ` (${res.backup_file})` : ""} · removed {Object.entries(res.removed).map(([k, v]) => `${v} ${k}`).join(", ") || "nothing"}
        </p>
      )}
    </form>
  );
}

function DrawingsTable({ rows }) {
  if (!rows.length) return <p data-testid="admin-drawings-empty" className="px-6 py-10 text-center text-sm text-slate-500">No drawings read yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px]" data-testid="admin-drawings-table">
        <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <th className="px-6 py-3">Date</th><th className="px-6 py-3">File</th><th className="px-6 py-3">Result</th>
          <th className="px-6 py-3 text-right">Items</th><th className="px-6 py-3">Credit</th><th className="px-6 py-3">Model</th></tr></thead>
        <tbody>{rows.map((d, i) => (
          <tr key={d.id} data-testid={`admin-drawing-row-${i}`} className="border-t border-slate-800/70 text-sm">
            <td className="px-6 py-3 text-slate-400">{d.created_at ? fmtDate(d.created_at) : "—"}</td>
            <td className="px-6 py-3 text-slate-100">{d.filename || "—"}</td>
            <td className="px-6 py-3 text-slate-300">{d.status}</td>
            <td className="px-6 py-3 text-right font-mono-num text-slate-300">{d.items}</td>
            <td className="px-6 py-3 text-slate-400">{d.charged ? "charged" : "not charged / refunded"}</td>
            <td className="px-6 py-3 text-xs text-slate-500">{d.model || "—"}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminCredits() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/home").then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div data-testid="admin-company-loading" className="h-64 animate-pulse rounded-2xl bg-slate-900/40" />;
  const c = d.company;
  return (
    <div className="space-y-8" data-testid="admin-credits-page">
      <div className="rise flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 data-testid="admin-company-title" className="font-display text-4xl font-bold text-slate-50 sm:text-5xl">{c.name} credits</h1>
          <p className="mt-3 text-sm text-slate-400 md:text-base">Adjust the reading credits and see the credit history and card purchases.</p>
        </div>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-6 py-3 text-right">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-500">Credits</p>
          <p data-testid="admin-company-credits" className="font-mono-num text-3xl font-bold text-slate-50">{c.credits}</p>
        </div>
      </div>
      <Panel title="Add or remove credits" testId="admin-gift-panel"><GiftForm onDone={load} /></Panel>
      <Tabs defaultValue="ledger" className="rise rise-1">
        <TabsList className="bg-slate-900/70">
          <TabsTrigger value="ledger" data-testid="admin-tab-ledger">Credit history</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="admin-tab-purchases">Purchases</TabsTrigger>
          <TabsTrigger value="drawings" data-testid="admin-tab-drawings">Drawings read</TabsTrigger>
          <TabsTrigger value="ai" data-testid="admin-tab-ai-cost">AI cost per reading</TabsTrigger>
        </TabsList>
        <TabsContent value="ledger"><Panel title="Credit history"><LedgerTable rows={d.ledger} prefix="admin-ledger" /></Panel></TabsContent>
        <TabsContent value="purchases"><Panel title="Purchases"><PurchaseTable rows={d.purchases} prefix="admin-purchase" /></Panel></TabsContent>
        <TabsContent value="drawings"><Panel title="Drawings read"><DrawingsTable rows={d.drawings} /></Panel></TabsContent>
        <TabsContent value="ai"><Panel title="AI cost per reading"><ReadingsTable /></Panel></TabsContent>
      </Tabs>
      <Panel title="Remove all other companies" testId="admin-remove-others-panel"><RemoveOthers count={c.other_companies} onDone={load} /></Panel>
    </div>
  );
}
